import { SimpleEventEmitter } from "./SimpleEventEmitter.js";

export class WebAudioClient extends SimpleEventEmitter {
    constructor(appClient) {
        super();
        this.appClient = appClient;
        this.socket = appClient.socket;

        // Config
        this.targetSampleRate = 16000;
        this.bufferSize = 4096;

        // State
        this.audioContext = null;
        this.workletNode = null;
        this.mediaStream = null;
        this.isPlaying = false;

        // Playback Buffer & Interruption Tracking
        this.nextStartTime = 0;
        this.activeSources = new Set(); // Track playing audio nodes so we can stop them

        this._boundPlayAudioChunk = this.playAudioChunk.bind(this);
    }

    async start() {
        if (this.audioContext) return;

        this.log("Starting Audio Context...");
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();

        try {
            const isGiven = await this.appClient.permissions.isPermissionGiven('microphone');
            if (!isGiven) {
                const isDenied = await this.appClient.permissions.isPermissionDenied('microphone');
                if (isDenied) {
                    throw new Error('Microphone access denied');
                }
                const result = await this.appClient.permissions.getPermission('microphone');
                if (result.status !== 'granted') {
                    throw new Error('Microphone access denied');
                }
            }

            this.mediaStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                }
            });
        } catch (e) {
            this.emit('error', 'Microphone access denied');
            throw e;
        }

        const workletCode = `
            class PCMProcessor extends AudioWorkletProcessor {
                constructor() {
                    super();
                    this.buffer = [];
                }
                process(inputs, outputs, parameters) {
                    const input = inputs[0];
                    if (input && input.length > 0) {
                        const channelData = input[0];
                        this.port.postMessage(channelData);
                    }
                    return true;
                }
            }
            registerProcessor('pcm-processor', PCMProcessor);
        `;
        const blob = new Blob([workletCode], { type: 'application/javascript' });
        const url = URL.createObjectURL(blob);

        await this.audioContext.audioWorklet.addModule(url);

        const source = this.audioContext.createMediaStreamSource(this.mediaStream);
        this.workletNode = new AudioWorkletNode(this.audioContext, 'pcm-processor');

        source.connect(this.workletNode);

        this.workletNode.port.onmessage = (event) => {
            if (!this.socket.connected || !this.isPlaying) return;
            const float32Data = event.data;
            const pcm16Data = this.resampleAndConvert(float32Data, this.audioContext.sampleRate, this.targetSampleRate);
            this.socket.emit('audio_in', pcm16Data);
        };

        this.socket.on('audio_out', this._boundPlayAudioChunk);

        this.isPlaying = true;
        this.emit('start');
    }

    // NEW: Instantly stop all currently playing audio chunks
    interrupt() {
        this.log("Interrupting audio playback...");
        for (const source of this.activeSources) {
            try { source.stop(); } catch (e) { }
        }
        this.activeSources.clear();
        if (this.audioContext) {
            this.nextStartTime = this.audioContext.currentTime;
        }
    }

    async stop() {
        this.log("Stopping Audio Client...");
        this.isPlaying = false;
        this.interrupt();

        if (this.workletNode) {
            this.workletNode.disconnect();
            this.workletNode = null;
        }

        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach(track => track.stop());
            this.mediaStream = null;
        }

        if (this.audioContext) {
            if (this.audioContext.state !== 'closed') {
                await this.audioContext.close();
            }
            this.audioContext = null;
        }

        this.socket.off('audio_out', this._boundPlayAudioChunk);
        this.emit('stop');
    }

    resampleAndConvert(float32Audio, originalSampleRate, targetSampleRate) {
        if (originalSampleRate === targetSampleRate) {
            return this.floatTo16BitPCM(float32Audio);
        }

        const ratio = originalSampleRate / targetSampleRate;
        const newLength = Math.round(float32Audio.length / ratio);
        const result = new Float32Array(newLength);

        let offsetResult = 0;
        let offsetSource = 0;

        while (offsetResult < result.length) {
            const nextOffsetSource = Math.round((offsetResult + 1) * ratio);
            let accum = 0, count = 0;
            for (let i = offsetSource; i < nextOffsetSource && i < float32Audio.length; i++) {
                accum += float32Audio[i];
                count++;
            }
            result[offsetResult] = accum / count;
            offsetResult++;
            offsetSource = nextOffsetSource;
        }

        return this.floatTo16BitPCM(result);
    }

    floatTo16BitPCM(float32Array) {
        const buffer = new ArrayBuffer(float32Array.length * 2);
        const view = new DataView(buffer);
        for (let i = 0; i < float32Array.length; i++) {
            let s = Math.max(-1, Math.min(1, float32Array[i]));
            view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
        }
        return buffer;
    }

    playAudioChunk(chunk) {
        if (!this.audioContext || !chunk) return;

        const view = new DataView(chunk);
        const float32 = new Float32Array(chunk.byteLength / 2);
        for (let i = 0; i < float32.length; i++) {
            const int16 = view.getInt16(i * 2, true);
            float32[i] = int16 / (int16 < 0 ? 0x8000 : 0x7FFF);
        }

        const buffer = this.audioContext.createBuffer(1, float32.length, this.targetSampleRate);
        buffer.getChannelData(0).set(float32);

        const source = this.audioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(this.audioContext.destination);

        const now = this.audioContext.currentTime;
        const startTime = Math.max(now, this.nextStartTime);

        source.start(startTime);
        this.nextStartTime = startTime + buffer.duration;

        // Track the source so we can stop it if interrupted
        this.activeSources.add(source);
        source.onended = () => {
            this.activeSources.delete(source);
        };
    }

    log(msg) {
        console.debug(`[WebAudioClient] ${msg}`);
    }
}